import { useEffect } from "react";

export type CallSound = "ring" | "ringback" | "waiting" | null;

// Each pattern repeats every `period` seconds: [start, length] pairs of soft two-tone bursts
const PATTERNS: Record<Exclude<CallSound, null>, { period: number; bursts: Array<[number, number]>; tones: number[]; volume: number }> = {
  // A phone ringing: two short rings
  ring: { period: 3, bursts: [[0, 0.4], [0.6, 0.4]], tones: [440, 480], volume: 0.07 },
  // What the caller hears while it rings
  ringback: { period: 4, bursts: [[0, 1.6]], tones: [440, 480], volume: 0.035 },
  // A second call while on one: a single quiet beep
  waiting: { period: 8, bursts: [[0, 0.25]], tones: [620], volume: 0.04 },
};

function playPattern(context: AudioContext, sound: Exclude<CallSound, null>) {
  const pattern = PATTERNS[sound];
  for (const [offset, length] of pattern.bursts) {
    const start = context.currentTime + offset;
    const gain = context.createGain();
    // Short ramps instead of hard steps, which click
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(pattern.volume, start + 0.03);
    gain.gain.setValueAtTime(pattern.volume, start + length - 0.05);
    gain.gain.linearRampToValueAtTime(0, start + length);
    gain.connect(context.destination);
    for (const frequency of pattern.tones) {
      const oscillator = context.createOscillator();
      oscillator.frequency.value = frequency;
      oscillator.connect(gain);
      oscillator.start(start);
      oscillator.stop(start + length + 0.02);
    }
  }
  return pattern.period;
}

/**
 * Plays the call sound while `sound` is set. With several tabs open, only the tab holding the lock
 * plays it, so the sounds don't pile up; another tab takes over if that one closes.
 */
export function useCallSounds(sound: CallSound) {
  useEffect(() => {
    if (!sound || typeof window === "undefined" || !window.AudioContext) return;
    let stopped = false;
    let release: (() => void) | null = null;
    const start = () => {
      const context = new AudioContext();
      // Chrome starts audio suspended until the page has had a click; the flashing title still signals the call
      void context.resume().catch(() => undefined);
      const period = playPattern(context, sound);
      const timer = window.setInterval(() => playPattern(context, sound), period * 1000);
      return () => {
        window.clearInterval(timer);
        void context.close();
      };
    };
    if (navigator.locks) {
      void navigator.locks.request("querygen-call-sound", async () => {
        if (stopped) return;
        const stop = start();
        // Held until this effect ends
        await new Promise<void>((resolve) => { release = () => { stop(); resolve(); }; });
      }).catch(() => undefined);
    } else {
      release = start();
    }
    return () => {
      stopped = true;
      release?.();
    };
  }, [sound]);
}

/** Flashes the tab title while a call rings, for a teammate looking at another tab. */
export function useFlashingTitle(text: string | null) {
  useEffect(() => {
    if (!text || typeof document === "undefined") return;
    const title = document.title;
    let flash = false;
    const timer = window.setInterval(() => {
      flash = !flash;
      document.title = flash ? text : title;
    }, 1000);
    return () => {
      window.clearInterval(timer);
      document.title = title;
    };
  }, [text]);
}
